import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SUPPORT_FORM_URL } from "@utils/constants";
import React, { useEffect, useState } from "react";

import { Dialog, Label, Paragraph, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import { useCreateClientFromEhrMutation, useEhrGetClientListMutation } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { useCreateReportHandler } from "@hooks/useCreateReportHandler";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRClientData } from "@providers/EHRClientProvider";

interface HRClientListProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  isUserLoggedIn: boolean;
  setSelectedClient: (client: SelectItem | null) => void;
  /** Managed-onboarding gate: false until Frontera has cleared this tenant's
   * EHR connection, or the integration is graduated. */
  isEhrSyncCleared: boolean;
}

export const EHRClientList: React.FC<HRClientListProps> = ({
  open,
  setOpen,
  isUserLoggedIn,
  setSelectedClient,
  isEhrSyncCleared,
}) => {
  const { selectedEHRClient, setSelectedEHRClient } = useEHRClientData();
  const { organizationLogo, isFronteraEhrUser } = useAdminData();
  const {
    learnerQuery: { refetch: refetchLearners },
  } = useCreateReportHandler();

  const [clientList, setClientList] = useState<SelectItem[]>([]);
  const [showImportFailedError, setShowImportFailedError] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<string | null>(null);
  const [showClientListFailedError, setShowClientListFailedError] = useState(false);
  const [showClientListEmpty, setShowClientListEmpty] = useState(false);

  const queryClient = useQueryClient();
  const getEHRClientListMutation = useEhrGetClientListMutation();
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["ehrClientList", isUserLoggedIn],
    queryFn: () => getEHRClientListMutation.mutateAsync({}),
    enabled: isUserLoggedIn && !selectedEHRClient && isEhrSyncCleared,
    retry: false,
  });

  const createClientFromEHRMutation = useCreateClientFromEhrMutation({});
  const { isPending: isClientCreating } = createClientFromEHRMutation;

  const emptyToUndef = (v?: string | null) => (v && v.trim() !== "" ? v.trim() : undefined);

  const normalizeDate = (d?: string | null) => {
    if (!d) return undefined;
    const t = String(d).trim();
    if (!t) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const parts = t.split("/");
    if (parts.length !== 3) return undefined;
    const [m, d2, y] = parts;
    return `${y}-${m.padStart(2, "0")}-${d2.padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isError) {
      setClientList([]);
      setShowClientListFailedError(true);
      setShowClientListEmpty(false);
      return;
    }

    if (data) {
      const clients: SelectItem[] =
        data.ehrGetClientList?.clients?.map((item) => ({
          id: item?.clientId || "",
          primary: `${item?.firstName || item?.preferredName || ""} ${item?.lastName || ""}`.trim(),
          className: "ph-no-capture",
        })) || [];
      setClientList(clients);

      if (clients.length === 0) {
        setShowClientListEmpty(true);
        setShowClientListFailedError(false);
      } else {
        setShowClientListEmpty(false);
        setShowClientListFailedError(false);
      }
    }
  }, [data, isError]);

  const showImportErrorMessage = (message?: string | null) => {
    setImportErrorMessage(message?.trim() ? message.trim() : null);
    setShowImportFailedError(true);
  };

  const handleImportClient = async () => {
    if (!selectedEHRClient) return;

    try {
      const picked = data?.ehrGetClientList?.clients?.find((c) => c?.clientId === selectedEHRClient.id) ?? null;

      if (!picked) {
        console.warn("Selected EHR client not found in the list");
        return;
      }
      const firstFromPrimary = selectedEHRClient.primary.split(" ")[0] || "";
      const lastFromPrimary = selectedEHRClient.primary.split(" ").slice(1).join(" ") || "";

      await createClientFromEHRMutation.mutateAsync(
        {
          learnerExternalId: selectedEHRClient.id || "",
          firstName: picked?.firstName ?? firstFromPrimary,
          lastName: picked?.lastName ?? lastFromPrimary,
          preferredName: emptyToUndef(picked?.preferredName),
          birthDate: normalizeDate(picked?.dateOfBirth),
          addressLine1: emptyToUndef(picked?.addressLine1),
          addressLine2: emptyToUndef(picked?.addressLine2),
          city: emptyToUndef(picked?.city),
          state: emptyToUndef(picked?.state),
          zipCode: emptyToUndef(picked?.zipCode),
          gender: emptyToUndef(picked?.gender),
          isImportForReassessment: false,
        },
        {
          onSuccess: (data) => {
            if (data.createClientFromEhr?.status) {
              // The imported client must not linger in the cached picker list.
              queryClient.removeQueries({ queryKey: ["ehrClientList"] });
              refetchLearners();
              setSelectedClient({
                id: data.createClientFromEhr?.learner?.id || "",
                primary: `${data.createClientFromEhr?.learner?.learnermetadata?.firstName || ""} ${
                  data.createClientFromEhr?.learner?.learnermetadata?.lastName || ""
                }`.trim(),
              });
              setOpen(false);
              setShowImportFailedError(false);
              setImportErrorMessage(null);
            } else {
              showImportErrorMessage(data.createClientFromEhr?.message);
            }
          },
          onError: (error) => {
            console.error("Error importing client from EHR:", error);
            showImportErrorMessage();
          },
        },
      );
    } catch (error) {
      console.error("Error importing client from EHR:", error);
    }
  };

  const handleRetry = () => {
    setSelectedEHRClient(undefined);
    setShowImportFailedError(false);
    setImportErrorMessage(null);
  };

  const handleRetryClientList = () => {
    setShowClientListFailedError(false);
    setSelectedEHRClient(undefined);
    refetch();
  };

  const renderError = (
    <div className="flex flex-col items-start gap-2 py-8 p-2">
      {importErrorMessage ? (
        <>
          <Label size="large" colorType="primary">
            Cannot import client from EHR
          </Label>
          <Label size="medium" colorType="primary">
            {importErrorMessage}
          </Label>
        </>
      ) : (
        <>
          <Label size="large" displayType="loud" colorType="primary">
            Sorry, something went wrong.
          </Label>
          <Label size="medium" colorType="primary">
            We were unable to import your client. Please retry and{" "}
            <a
              className="text-interaction-primary no-underline cursor-pointer"
              href={SUPPORT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              contact support
            </a>{" "}
            if the issue does not resolve.
          </Label>
        </>
      )}
    </div>
  );

  const renderClientListError = (
    <div className="flex flex-col items-start gap-2 py-8 p-2">
      <Label size="large" displayType="loud" colorType="primary">
        Sorry, something went wrong.
      </Label>
      <Label size="medium" colorType="primary">
        We were unable to load your client list. Please retry and{" "}
        <a
          className="text-interaction-primary no-underline cursor-pointer"
          href={SUPPORT_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          contact support
        </a>{" "}
        if the issue does not resolve.
      </Label>
    </div>
  );

  const isListIncomplete = data?.ehrGetClientList?.isComplete === false;

  const incompleteNotice = (
    <Paragraph colorType="secondary" className="pb-2">
      Not every client could be loaded from your EHR. If a client is missing, please{" "}
      <a
        className="text-interaction-primary no-underline cursor-pointer"
        href={SUPPORT_FORM_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        contact support
      </a>
      .
    </Paragraph>
  );

  const emptyClientList = () => {
    if (isListIncomplete) {
      return <div className="flex flex-col items-start gap-2 py-8 p-2">{incompleteNotice}</div>;
    }
    if (isFronteraEhrUser) {
      return (
        <div className="flex flex-col items-start gap-2 py-8 p-2">
          <Label size="large" displayType="loud" colorType="primary">
            All clients added.
          </Label>
          <Label size="medium" colorType="primary">
            Every client in Frontera EHR has already been added. Choose them from your client list instead.
          </Label>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-start gap-2 py-8 p-2">
        <Label size="large" displayType="loud" colorType="primary">
          No clients found.
        </Label>
        <Label size="medium" colorType="primary">
          Your EHR returned an empty client list. If you expected clients to appear, please{" "}
          <a
            className="text-interaction-primary no-underline cursor-pointer"
            href={SUPPORT_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            contact support
          </a>
          .
        </Label>
      </div>
    );
  };
  const renderClientListEmpty = emptyClientList();

  const renderLoading = (
    <div className="flex flex-col items-start p-2 gap-4">
      <Label size="medium" colorType="primary">
        Loading clients
      </Label>
      <Loader />
      <div>
        <Paragraph colorType="secondary">Fetching your client list from your EHR.</Paragraph>
        <Paragraph colorType="secondary">This may take a few moments.</Paragraph>
      </div>
    </div>
  );

  const renderSelect = (
    <div className="flex flex-col p-2 pb-0 ph-no-capture">
      {isListIncomplete && incompleteNotice}
      <Select
        title="Select Client"
        items={clientList}
        placeholderText="Type to find a client"
        selected={selectedEHRClient}
        setSelected={setSelectedEHRClient}
        enableSearch
        formNoValidate={true}
      />
    </div>
  );

  const anyError = showImportFailedError || showClientListFailedError;

  const primaryOnClick = showImportFailedError
    ? handleRetry
    : showClientListFailedError
      ? handleRetryClientList
      : handleImportClient;

  return (
    <Dialog
      title=""
      open={open}
      setOpen={() => {}}
      showCloseButton={false}
      primaryButton={{
        text: anyError ? (
          "Retry"
        ) : (
          <div className="flex gap-1 w-max">
            {isClientCreating && <Loader />}
            Import
          </div>
        ),
        onClick: primaryOnClick,
        disabled: isClientCreating || (!anyError && !selectedEHRClient),
      }}
      secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
      size="xl"
    >
      <div className="flex flex-col items-center mt-3 mb-8">
        <img className="w-[80px] h-[80px]" src={organizationLogo} alt={`logo`} />
      </div>
      <div className="flex flex-col p-2">
        {showImportFailedError
          ? renderError
          : showClientListFailedError
            ? renderClientListError
            : isPending
              ? renderLoading
              : showClientListEmpty
                ? renderClientListEmpty
                : renderSelect}
      </div>
    </Dialog>
  );
};
